import type { GaiaSchemaResponse } from "@vega-api/generated/types/gaiaSchemaResponse";

export const fundSchemaMockClosedEnded: GaiaSchemaResponse = {
  data: {
    uiSchema: {
      "ui:options": {
        layout: [
          {
            title: "Basic information",
            fields: [
              "investmentProductType",
              "fundIdentifier",
              "assetClass",
              "isRegistered",
              "baseCurrency",
              "vintages",
              "proposedLaunchDate",
            ],
          },
          {
            title: "Terms and target composition",
            fields: [
              "fundRising",
              "productTermYears",
              "termExtension",
              "industry",
              "investmentStrategies",
              "geographicalFocusRegion",
              "impactFocusedStrategy",
            ],
          },
        ],
      },
      investmentProductType: { "ui:widget": "hidden" },
      fundIdentifier: {
        "ui:title": "",
        "ui:options": { border: false },
        code: { "ui:title": "Fund code", "ui:immutable": true },
        name: { "ui:title": "Fund name", "ui:immutable": true },
      },
      assetClass: {
        "ui:title": "Asset class",
        "ui:widget": "select",
        "ui:immutable": true,
        "ui:enumNames": [
          "Private Equity",
          "Private Credit",
          "Real Assets",
          "Hybrid",
          "Real Estate",
          "Infrastructure",
        ],
      },
      baseCurrency: { "ui:title": "Base currency", "ui:widget": "select" },
      isRegistered: {
        "ui:title": "Registered product",
        "ui:widget": "radio",
        "ui:description": "Select this if fund is registered",
        "ui:options": { layout: "inline-row" },
      },
      fundRising: {
        "ui:title": "",
        "ui:options": { border: false },
        targetRaise: {
          "ui:title": "",
          "ui:options": { border: false },
          amount: { "ui:title": "Target raise", "ui:widget": "money" },
          currency: { "ui:widget": "hidden" },
        },
        hardCap: {
          "ui:title": "",
          "ui:options": { border: false },
          amount: {
            "ui:title": "Hard cap",
            "ui:widget": "money",
            "ui:options": { footnote: true },
          },
          currency: { "ui:widget": "hidden" },
        },
      },
      productTermYears: {
        "ui:title": "",
        "ui:options": { border: false },
        productTermYears: {
          "ui:title": "Product term years",
          "ui:placeholder": "10",
        },
      },
      vintages: {
        "ui:title": "",
        "ui:options": { border: false },
        previousVintageYear: {
          "ui:title": "Vintage Year",
          "ui:placeholder": "2024",
        },
        previousVintageFundCodes: {
          "ui:title": "",
          "ui:options": { border: false },
          code: {
            "ui:label": "Associated prior vintage",
            "ui:field": "asyncMultiSelect",
            "ui:description":
              "List of fund codes that connect this fund to earlier vintages",
          },
        },
      },
      impactFocusedStrategy: {
        "ui:title": "",
        "ui:options": { border: false },
        isImpactFocused: {
          "ui:field": "checkboxCard",
          "ui:options": { footnote: true },
          "ui:title": "Impact focus strategy",
          "ui:description":
            "Fund is strictly dedicated to impact-driven investments",
        },
        impactFocusedStrategy: { "ui:widget": "hidden" },
      },
      proposedLaunchDate: {
        "ui:options": { border: false },
        date: { "ui:widget": "datePicker", "ui:title": "Proposed launch date" },
      },
      footnotes: { "ui:title": "Footnotes", "ui:widget": "hidden" },
      customFields: { "ui:widget": "hidden" },
      documents: { "ui:widget": "hidden" },
      medias: { "ui:widget": "hidden" },
      fundManagerTeam: { "ui:widget": "hidden" },
      industry: {
        "ui:options": { border: false },
        industries: {
          "ui:label": "Industries",
          "ui:field": "multiSelect",
          "ui:enumNames": [
            "Chemicals",
            "Commercial Services",
            "Construction",
            "Consumer Durables",
            "Cyber",
            "Education",
            "Energy",
            "ESG",
            "Financial Services",
            "Fintech",
            "Food Production",
            "Food Retail",
            "Forestry",
            "General Technology",
            "Healthcare",
            "Hospitality",
            "Industrial Goods and Services",
            "Industry Agnostic",
            "Insurance",
            "Manufacturing",
            "Media",
            "Metals and Minerals Production",
            "Metals and Mining",
            "Pharmaceuticals",
            "Professional Services",
            "Public Services",
            "Real Estate",
            "Retail",
            "Sustainability",
            "Telecommunications",
            "Transport",
            "Utilities",
          ],
        },
      },
      investmentStrategies: {
        "ui:options": { border: false },
        investmentStrategies: {
          "ui:label": "Investment strategies",
          "ui:field": "multiSelect",
          "ui:enumNames": [
            "Buyouts",
            "Distressed for Control",
            "Special Situations",
            "General Partner Stakes",
            "Growth Equity",
            "Venture Capital",
            "Early Stage Venture Capital",
            "Mid Stage Venture Capital",
            "Late Stage Venture Capital",
            "Multi Stage Venture Capital",
            "Secondary Investments",
            "Single General Partner",
            "Direct Lending",
            "Senior Secured",
            "Mezzanine",
            "Distressed Credit",
            "Speciality Finance",
            "Venture Debt",
            "Asset Backed Lending",
            "Infrastructure Credit",
            "Subordinated",
            "Structured Credit",
            "Structured Equity",
            "Multi Strategy",
            "Residential Property",
            "Office Property",
            "Hospitality Property",
            "Life Sciences Property",
            "Industrial Property",
            "Property Debt and Loans",
            "Retail Property",
            "Other Property",
            "Power Infrastructure",
            "Renewables Infrastructure",
            "Communications Infrastructure",
            "Transportation Infrastructure",
            "Social Infrastructure",
            "Midstream Infrastructure",
            "Farm Land",
            "Timber Land",
            "Renewable Energy",
            "Precious Metals",
            "Commodities",
            "Lower Market",
            "Middle Market",
            "Upper Market",
            "Core",
            "Core Plus",
            "Value Added",
            "Opportunistic",
            "Distressed",
            "Diversified",
          ],
        },
      },
      geographicalFocusRegion: {
        "ui:options": { border: false },
        geographicalFocusRegion: {
          "ui:label": "Geographical focus",
          "ui:field": "multiSelect",
          "ui:options": { creatable: true },
          "ui:enumNames": [
            "Africa",
            "Asia Pacific",
            "Latin America and Caribbean",
            "Europe",
            "Middle East",
            "North America",
            "Global",
          ],
        },
      },
      termExtension: {
        "ui:title": "",
        "ui:options": { border: false },
        termExtension: {
          "ui:title": "Term extension",
          "ui:description": "Potential extension beyond product term in years",
        },
      },
      vehicles: {
        "ui:options": { renderAsVehicleCard: true },
        items: {
          "ui:options": {
            modalFields: ["identifier"],
            layout: [
              {
                title: "Basic information",
                fields: [
                  "identifier",
                  "fundingStatus",
                  "currency",
                  "closingSchedule",
                  "legalEntity",
                  "strategy",
                  "generalPartnerCommitment",
                  "estimatedCloseDate",
                  "isEmployee",
                ],
              },
              {
                title: "Set targets",
                fields: ["targets"],
              },
              {
                title: "Additional information",
                fields: ["supportContacts"],
              },
            ],
          },
          identifier: {
            "ui:title": "",
            "ui:options": { border: false },
            code: { "ui:title": "Vehicle code", "ui:immutable": true },
            name: { "ui:title": "Vehicle name", "ui:immutable": true },
          },
          fundingStatus: {
            "ui:title": "Funding status",
            "ui:widget": "select",
            "ui:enumNames": ["Open", "Closed", "Upcoming", "Liquidated"],
            "ui:enumDescriptions": {
              OPEN: "Investors can view fund details but cannot subscribe",
              CLOSED: "Investors can view the fund and submit subscriptions",
              UPCOMING: "Investors can only review performance until wind-down",
              LIQUIDATED:
                "Fundraising has ended, investors can review performance until wind-down",
            },
            "ui:description":
              "Controls whether and how the fund is visible to investors. Upcoming: investors can view fund details but cannot subscribe. Open: investors can view the fund and submit subscriptions. Closed/Liquidated: fundraising has ended; investors can review performance until wind-down.",
          },
          currency: {
            "ui:title": "Currency",
            "ui:widget": "select",
            "ui:description":
              "The base currency of the vehicle, applied to all monetary values. Note: funds and share classes may have different currencies.",
          },
          legalEntity: {
            "ui:title": "",
            "ui:options": { border: false },
            country: {
              "ui:title": "Jurisdiction country",
              "ui:widget": "select",
              "ui:description": "Country where this vehicle is domiciled",
            },
            state: {
              "ui:title": "Jurisdiction state",
              "ui:widget": "select",
            },
            taxReportingDocumentation: {
              "ui:label": "Tax reporting documentation",
              "ui:field": "multiSelect",
              "ui:enumNames": [
                "Form 1099",
                "Form K-1",
                "Not Applicable",
                "Form 1042-S",
              ],
            },
          },
          strategy: {
            "ui:options": { border: false },
            description: {
              "ui:title": "Strategy",
              "ui:widget": "textarea",
              "ui:description":
                "Product strategy in HTML format. Prominently displayed on the product page.",
            },
            generalDisclaimerContent: {
              "ui:title": "General disclaimer",
              "ui:widget": "textarea",
              "ui:description":
                "General disclaimer displayed on the product page",
              "ui:options": { showDescriptionIcon: true },
            },
          },
          generalPartnerCommitment: {
            "ui:title": "",
            "ui:options": { border: false },
            amount: {
              "ui:title": "General partner commitment",
              "ui:widget": "percent",
              "ui:placeholder": "e.g. 2%",
            },
          },
          supportContacts: {
            "ui:field": "supportContact",
            "ui:title": "Support contact",
            supportContacts: {
              items: {
                name: { "ui:title": "Full name" },
                email: { "ui:title": "Email address" },
                phone: { "ui:title": "Phone" },
              },
            },
          },
          isEmployee: {
            "ui:field": "checkboxCard",
            "ui:title": "Employee vehicle",
            "ui:description": "Vehicle is restricted to eligible employees",
            "ui:options": { footnote: true },
          },
          footnotes: { "ui:title": "Footnotes", "ui:widget": "hidden" },
          customFields: { "ui:widget": "hidden" },
          inceptionDate: {
            "ui:widget": "hidden",
            "ui:options": { border: false },
            inceptionDate: {
              "ui:widget": "datePicker",
              "ui:title": "Inception date",
            },
          },
          closingSchedule: {
            "ui:title": "Closing schedule",
            "ui:field": "closingSchedule",
            "ui:options": { footnote: true },
          },
          estimatedCloseDate: {
            "ui:title": "Estimated close date",
            "ui:widget": "datePicker",
          },
          termExtension: {
            "ui:widget": "hidden",
            "ui:title": "",
            "ui:options": { border: false },
            termExtension: {
              "ui:title": "Term extension",
              "ui:description":
                "Potential extension beyond product term in years",
            },
          },
          targets: {
            "ui:field": "targets",
          },
          shareClasses: {
            "ui:options": { renderAsShareClassesOnly: true },
            items: {
              "ui:order": [
                "shareClassIdentifier",
                "description",
                "nonQualifiedMinimumInvestment",
                "nonQualifiedMinimumSubsequentInvestment",
                "valuationFrequency",
                "managementFees",
                "incentiveFees",
                "additionalFees",
                "carriedInterestRate",
                "hurdleRate",
                "redemptionGates",
                "investorTypes",
                "investorTargetRegion",
                "inceptionDate",
                "footnotes",
                "customFields",
              ],
              "ui:options": { modalFields: ["shareClassIdentifier"] },
              shareClassIdentifier: {
                "ui:title": "",
                "ui:options": { border: false },
                code: { "ui:title": "Share class code", "ui:immutable": true },
                name: { "ui:title": "Share class name", "ui:immutable": true },
              },
              description: {
                "ui:options": { border: false },
                description: {
                  "ui:title": "Description",
                  "ui:widget": "textarea",
                },
              },
              nonQualifiedMinimumInvestment: {
                "ui:title": "",
                "ui:options": { border: false },
                currencyAmount: {
                  "ui:title": "",
                  "ui:options": { border: false },
                  amount: {
                    "ui:title": "Minimum investment amount",
                    "ui:widget": "money",
                    "ui:options": { footnote: true },
                    "ui:description":
                      "The amount allowed to be invested in the share class",
                  },
                  currency: { "ui:widget": "hidden" },
                },
              },
              nonQualifiedMinimumSubsequentInvestment: {
                "ui:title": "",
                "ui:options": { border: false },
                currencyAmount: {
                  "ui:title": "",
                  "ui:options": { border: false },
                  amount: {
                    "ui:title": "Minimum subsequent investment amount",
                    "ui:widget": "money",
                    "ui:options": { footnote: true },
                    "ui:description":
                      "The subsequent amount allowed to be invested in the share class",
                  },
                  currency: { "ui:widget": "hidden" },
                },
              },
              valuationFrequency: {
                "ui:title": "Valuation frequency",
                "ui:field": "valuationFrequency",
                frequency: {
                  "ui:enumNames": [
                    "Daily",
                    "Weekly",
                    "Fortnightly",
                    "Semi-Monthly",
                    "Monthly",
                    "Quarterly",
                    "Semi-Annually",
                    "Annually",
                    "Ad Hoc",
                  ],
                },
              },
              managementFees: {
                "ui:field": "managementFees",
                "ui:title": "Management fees",
                "ui:description": "Management fee in percentage",
                "ui:options": { footnote: true },
              },
              incentiveFees: {
                "ui:field": "incentiveFees",
                "ui:title": "Incentive fees",
                "ui:description": "Incentive fee taken by the GP",
                "ui:options": { footnote: true },
              },
              additionalFees: {
                "ui:title": "Additional fees",
                "ui:field": "additionalFees",
                items: {
                  type: {
                    "ui:enumNames": [
                      "Trail",
                      "Brokerage",
                      "Placement",
                      "Transaction",
                      "Administration",
                      "Servicing",
                      "Custody",
                      "Sponsor",
                      "Exit",
                      "Incentive",
                      "Pass Through",
                      "Sales Load",
                      "Other",
                    ],
                  },
                  fee: {
                    structure: {
                      "ui:enumNames": ["Single Rate", "Tiered Rate"],
                    },
                    paymentFrequency: {
                      "ui:enumNames": [
                        "Monthly in Advance",
                        "Monthly in Arrears",
                        "Quarterly in Advance",
                        "Quarterly in Arrears",
                        "Semi-Annually in Advance",
                        "Semi-Annually in Arrears",
                        "Annually in Advance",
                        "Annually in Arrears",
                        "Once on Subscription",
                        "Once on Redemption",
                        "Once on Transfer",
                        "Once Off",
                        "Ad Hoc",
                      ],
                    },
                    feeBasis: {
                      "ui:enumNames": [
                        "Net Asset Value per Unit/Share",
                        "Gross Asset Value",
                        "Committed Capital",
                        "Paid in Capital",
                        "Offer Price",
                        "Income",
                        "Realized Capital Gains",
                      ],
                    },
                    applicablePeriod: {
                      "ui:enumNames": [
                        "Investment Period",
                        "Post Investment Period",
                      ],
                    },
                  },
                },
              },
              carriedInterestRate: {
                "ui:title": "",
                "ui:options": { border: false },
                carriedInterestRate: {
                  "ui:title": "Carried interest rate",
                  "ui:placeholder": "eg 10%",
                  "ui:options": { footnote: true, displayUnit: "percent" },
                },
                carriedInterestAdditionalInformation: { "ui:widget": "hidden" },
              },
              inceptionDate: {
                "ui:options": { border: false },
                inceptionDate: {
                  "ui:widget": "datePicker",
                  "ui:title": "Inception date",
                  "ui:options": { footnote: true },
                },
              },
              investorTypes: {
                "ui:options": { border: false },
                investorTypes: {
                  "ui:label": "Investor types",
                  "ui:field": "multiSelect",
                  "ui:options": { footnote: true },
                  "ui:enumNames": [
                    "Knowledgeable Employee",
                    "Qualified Purchaser",
                    "Accredited Investor",
                    "Qualified Client",
                    "Institutional Investor",
                    "EU Professional Client",
                    "Retirement Plan",
                    "Non-Accredited or Retail",
                  ],
                },
              },
              investorTargetRegion: {
                "ui:options": { border: false },
                investorTargetRegion: {
                  "ui:label": "Investor target regions",
                  "ui:field": "multiSelect",
                  "ui:options": { footnote: true, creatable: true },
                },
              },
              distributionSchedule: {
                "ui:widget": "hidden",
                "ui:title": "",
                "ui:options": { border: false },
                frequency: {
                  "ui:title": "Distribution schedule",
                  "ui:widget": "select",
                  "ui:options": { footnote: true },
                  "ui:enumNames": [
                    "Daily",
                    "Weekly",
                    "Fortnightly",
                    "Semi-Monthly",
                    "Monthly",
                    "Quarterly",
                    "Semi-Annually",
                    "Annually",
                    "Ad Hoc",
                  ],
                },
              },
              hurdleRate: {
                "ui:widget": "hidden",
                "ui:title": "",
                "ui:options": { border: false },
                rate: {
                  "ui:title": "",
                  "ui:options": { border: false },
                  rate: {
                    "ui:title": "Hurdle rate",
                    "ui:options": { footnote: true },
                  },
                },
              },
              catchUp: {
                "ui:widget": "hidden",
                "ui:title": "",
                "ui:options": { border: false },
                catchUp: {
                  "ui:title": "Catch up",
                  "ui:options": { footnote: true, displayUnit: "percent" },
                },
              },
              redemptionGates: { "ui:widget": "hidden" },
              footnotes: { "ui:title": "Footnotes", "ui:widget": "hidden" },
              customFields: { "ui:widget": "hidden" },
            },
          },
        },
      },
    },
    jsonSchema: {
      $schema: "https://json-schema.org/draft/2020-12/schema",
      type: "object",
      title: "Closed-Ended",
      description:
        "Closed-ended fund with defined fundraising period and lock-up",
      required: [
        "investmentProductType",
        "fundIdentifier",
        "assetClass",
        "baseCurrency",
        "isRegistered",
      ],
      $defs: {
        Currency: {
          type: "string",
          enum: [
            "GBP",
            "USD",
            "EUR",
            "AUD",
            "JPY",
            "CAD",
            "CNY",
            "CHF",
            "SEK",
            "NZD",
            "MXN",
            "SGD",
            "HKD",
            "NOK",
            "KRW",
            "TRY",
            "INR",
            "RUB",
            "BRL",
            "ZAR",
            "GBX",
            "DKK",
            "PLN",
            "HUF",
            "CZK",
            "HRK",
            "BGN",
            "ISK",
            "RSD",
          ],
        },
        CurrencyAmount: {
          type: "object",
          properties: {
            amount: { type: "number" },
            currency: { $ref: "#/$defs/Currency" },
          },
        },
        CustomField: {
          type: "object",
          properties: {
            name: { type: "string" },
            formatedValue: { type: "string" },
            value: { type: "string" },
          },
        },
        Fee: {
          type: "object",
          properties: {
            name: { type: "string" },
            structure: {
              type: "string",
              enum: ["SINGLE_RATE", "TIERED_RATE"],
            },
            paymentFrequency: {
              type: "string",
              enum: [
                "MONTHLY_IN_ADVANCE",
                "MONTHLY_IN_ARREARS",
                "QUARTERLY_IN_ADVANCE",
                "QUARTERLY_IN_ARREARS",
                "SEMI_ANNUALLY_IN_ADVANCE",
                "SEMI_ANNUALLY_IN_ARREARS",
                "ANNUALLY_IN_ADVANCE",
                "ANNUALLY_IN_ARREARS",
                "ONCE_ON_SUBSCRIPTION",
                "ONCE_ON_REDEMPTION",
                "ONCE_ON_TRANSFER",
                "ONCE_OFF",
                "AD_HOC",
              ],
            },
            rate: { $ref: "#/$defs/Rate" },
            feeBasis: {
              type: "string",
              enum: [
                "NET_ASSET_VALUE_PER_UNIT_SHARE",
                "GROSS_ASSET_VALUE",
                "COMMITTED_CAPITL",
                "PAID_IN_CAPITAL",
                "OFFER_PRICE",
                "INCOME",
                "REALIZED_CAPITAL_GAINS",
              ],
            },
            applicablePeriod: {
              type: "string",
              enum: ["INVESTMENT_PERIOD", "POST_INVESTMENT_PERIOD"],
            },
            additionalInformation: { type: "string" },
          },
        },
        Footnote: {
          type: "object",
          properties: {
            content: { type: "string" },
            fieldKeys: {
              type: "array",
              items: { type: "string" },
            },
          },
        },
        Frequency: {
          type: "string",
          enum: [
            "DAILY",
            "WEEKLY",
            "FORTNIGHTLY",
            "SEMI_MONTHLY",
            "MONTHLY",
            "QUARTERLY",
            "SEMI_ANNUALLY",
            "ANNUALLY",
            "AD_HOC",
          ],
        },
        InceptionDate: {
          type: "object",
          properties: {
            inceptionDate: { type: "string", format: "date-time" },
          },
        },
        OffsetOrWindow: {
          type: "object",
          properties: {
            value: { type: "integer" },
            dayType: {
              type: "string",
              enum: ["BUSINESS_DAY", "CALENDAR_DAY"],
            },
          },
        },
        Rate: {
          type: "object",
          properties: {
            rate: { type: "number" },
          },
        },
        SupportContacts: {
          type: "object",
          properties: {
            supportContacts: {
              type: "array",
              maxItems: 1,
              items: {
                type: "object",
                properties: {
                  name: { type: "string" },
                  email: { type: "string" },
                  phone: { type: "string" },
                },
              },
            },
          },
        },
        TargetInvestment: {
          type: "object",
          properties: {
            minimum: { type: "number" },
            maximum: { type: "number" },
            currency: { $ref: "#/$defs/Currency" },
          },
        },
      },
      properties: {
        investmentProductType: {
          const: "CLOSED_ENDED",
          readOnly: true,
          title: "Type of investment product structure",
          description: "Fund type is fixed to CLOSED_ENDED for this schema",
        },
        fundIdentifier: {
          type: "object",
          title: "Unique identifier code for the fund",
          required: ["code", "name"],
          properties: {
            code: { type: "string", minLength: 1 },
            name: { type: "string", minLength: 1 },
          },
        },
        assetClass: {
          type: "string",
          title: "Primary asset class of the fund",
          enum: [
            "PRIVATE_EQUITY",
            "PRIVATE_CREDIT",
            "REAL_ASSETS",
            "HYBRID",
            "REAL_ESTATE",
            "INFRASTRUCTURE",
          ],
        },
        baseCurrency: {
          $ref: "#/$defs/Currency",
        },
        isRegistered: {
          type: "boolean",
        },
        fundRising: {
          type: "object",
          properties: {
            hardCap: { $ref: "#/$defs/CurrencyAmount" },
            targetRaise: { $ref: "#/$defs/CurrencyAmount" },
          },
        },
        impactFocusedStrategy: {
          type: "object",
          properties: {
            isImpactFocused: { type: "boolean" },
            impactFocusedStrategy: { type: "string" },
          },
        },
        proposedLaunchDate: {
          type: "object",
          properties: {
            date: { type: "string", format: "date-time" },
          },
        },
        productTermYears: {
          type: "object",
          properties: {
            productTermYears: { type: "number" },
          },
        },
        vintages: {
          type: "object",
          properties: {
            previousVintageYear: { type: "string" },
            previousVintageFundCodes: {
              type: "object",
              properties: {
                code: {
                  type: "array",
                  items: { type: "string" },
                },
              },
            },
          },
        },
        termExtension: {
          type: "object",
          properties: {
            termExtension: { type: "string" },
          },
        },
        industry: {
          type: "object",
          properties: {
            industries: {
              type: "array",
              items: {
                type: "string",
                enum: [
                  "CHEMICALS",
                  "COMMERCIAL_SERVICES",
                  "CONSTRUCTION",
                  "CONSUMER_DURABLES",
                  "CYBER",
                  "EDUCATION",
                  "ENERGY",
                  "ESG",
                  "FINANCIAL_SERVICES",
                  "FINTECH",
                  "FOOD_PRODUCTION",
                  "FOOD_RETAIL",
                  "FORESTRY",
                  "GENERAL_TECHNOLOGY",
                  "HEALTHCARE",
                  "HOSPITALITY",
                  "INDUSTRIAL_GOODS_AND_SERVICES",
                  "INDUSTRY_AGNOSTIC",
                  "INSURANCE",
                  "MANUFACTURING",
                  "MEDIA",
                  "METALS_AND_MINERALS_PRODUCTION",
                  "METALS_AND_MINING",
                  "PHARMACEUTICALS",
                  "PROFESSIONAL_SERVICES",
                  "PUBLIC_SERVICES",
                  "REAL_ESTATE",
                  "RETAIL",
                  "SUSTAINABILITY",
                  "TELECOMMUNICATIONS",
                  "TRANSPORT",
                  "UTILITIES",
                ],
              },
            },
          },
        },
        investmentStrategies: {
          type: "object",
          properties: {
            investmentStrategies: {
              type: "array",
              items: {
                type: "string",
                enum: [
                  "BUYOUTS",
                  "DISTRESSED_FOR_CONTROL",
                  "SPECIAL_SITUATIONS",
                  "GENERAL_PARTNER_STAKES",
                  "GROWTH_EQUITY",
                  "VENTURE_CAPITAL",
                  "EARLY_STAGE_VENTURE_CAPITAL",
                  "MID_STAGE_VENTURE_CAPITAL",
                  "LATE_STAGE_VENTURE_CAPITAL",
                  "MULTI_STAGE_VENTURE_CAPITAL",
                  "SECONDARY_INVESTMENTS",
                  "SINGLE_GENERAL_PARTNER",
                  "DIRECT_LENDING",
                  "SENIOR_SECURED",
                  "MEZZANINE",
                  "DISTRESSED_CREDIT",
                  "SPECIALITY_FINANCE",
                  "VENTURE_DEBT",
                  "ASSET_BACKED_LENDING",
                  "INFRASTRUCTURE_CREDIT",
                  "SUBORDINATED",
                  "STRUCTURED_CREDIT",
                  "STRUCTURED_EQUITY",
                  "MULTI_STRATEGY",
                  "RESIDENTIAL_PROPERTY",
                  "OFFICE_PROPERTY",
                  "HOSPITALITY_PROPERTY",
                  "LIFE_SCIENCES_PROPERTY",
                  "INDUSTRIAL_PROPERTY",
                  "PROPERTY_DEBT_AND_LOANS",
                  "RETAIL_PROPERTY",
                  "OTHER_PROPERTY",
                  "POWER_INFRASTRUCTURE",
                  "RENEWABLES_INFRASTRUCTURE",
                  "COMMUNICATIONS_INFRASTRUCTURE",
                  "TRANSPORTATION_INFRASTRUCTURE",
                  "SOCIAL_INFRASTRUCTURE",
                  "MIDSTREAM_INFRASTRUCTURE",
                  "FARM_LAND",
                  "TIMBER_LAND",
                  "RENEWABLE_ENERGY",
                  "PRECIOUS_METALS",
                  "COMMODITIES",
                  "LOWER_MARKET",
                  "MIDDLE_MARKET",
                  "UPPER_MARKET",
                  "CORE",
                  "CORE_PLUS",
                  "VALUE_ADDED",
                  "OPPORTUNISTIC",
                  "DISTRESSED",
                  "DIVERSIFIED",
                ],
              },
            },
          },
        },
        geographicalFocusRegion: {
          type: "object",
          properties: {
            geographicalFocusRegion: {
              type: "array",
              items: {
                type: "string",
                enum: [
                  "AFRICA",
                  "ASIA_PACIFIC",
                  "LATIN_AMERICA_AND_CARIBBEAN",
                  "EUROPE",
                  "MIDDLE_EAST",
                  "NORTH_AMERICA",
                  "GLOBAL",
                ],
              },
            },
          },
        },
        vehicles: {
          type: "array",
          description: "At least one vehicle is required",
          minItems: 1,
          items: {
            type: "object",
            required: [
              "identifier",
              "fundingStatus",
              "currency",
              "legalEntity",
              "closingSchedule",
            ],
            properties: {
              identifier: {
                type: "object",
                required: ["code", "name"],
                properties: {
                  code: { type: "string", minLength: 1 },
                  name: { type: "string", minLength: 1 },
                },
              },
              fundingStatus: {
                type: "string",
                enum: ["OPEN", "CLOSED", "UPCOMING", "LIQUIDATED"],
              },
              currency: {
                $ref: "#/$defs/Currency",
              },
              legalEntity: {
                type: "object",
                required: ["country"],
                properties: {
                  taxReportingDocumentation: {
                    type: "array",
                    items: {
                      type: "string",
                      enum: [
                        "FORM_1099",
                        "FORM_K1",
                        "NOT_APPLICABLE",
                        "FORM_1042_S",
                      ],
                    },
                  },
                  country: {
                    type: "string",
                    enum: [
                      "AR",
                      "AU",
                      "BR",
                      "CA",
                      "CH",
                      "CN",
                      "CY",
                      "LU",
                      "FR",
                      "DE",
                      "IT",
                      "GB",
                      "GG",
                      "IN",
                      "JP",
                      "KR",
                      "MX",
                      "RU",
                      "SA",
                      "TR",
                      "US",
                      "ZA",
                    ],
                  },
                  state: {
                    type: "string",
                    enum: [
                      "AL",
                      "AK",
                      "AZ",
                      "AR",
                      "CA",
                      "CO",
                      "CT",
                      "DE",
                      "FL",
                      "GA",
                      "HI",
                      "IL",
                      "IN",
                      "IA",
                      "KS",
                      "KY",
                      "LA",
                      "ME",
                      "MD",
                      "MA",
                      "MI",
                      "MN",
                      "MS",
                      "MO",
                      "MT",
                      "NE",
                      "NV",
                      "NH",
                      "NJ",
                      "NM",
                      "NY",
                      "NC",
                      "ND",
                      "OH",
                      "OK",
                      "OR",
                      "PA",
                      "RI",
                      "SC",
                      "SD",
                      "TN",
                      "TX",
                      "UT",
                      "VT",
                      "VA",
                      "WA",
                      "WV",
                      "WI",
                      "WY",
                    ],
                  },
                },
              },
              strategy: {
                type: "object",
                properties: {
                  description: { type: "string" },
                  generalDisclaimerContent: { type: "string" },
                },
              },
              supportContacts: {
                type: "object",
                properties: {
                  supportContacts: {
                    type: "array",
                    maxItems: 1,
                    items: {
                      type: "object",
                      properties: {
                        name: { type: "string" },
                        email: { type: "string" },
                        phone: { type: "string" },
                      },
                    },
                  },
                },
              },
              inceptionDate: {
                $ref: "#/$defs/InceptionDate",
              },
              generalPartnerCommitment: {
                type: "object",
                properties: {
                  amount: { type: "number" },
                },
              },
              termExtension: {
                type: "object",
                properties: {
                  termExtension: { type: "string" },
                },
              },
              estimatedCloseDate: {
                type: "string",
                format: "date-time",
              },
              closingSchedule: {
                type: "array",
                items: {
                  type: "object",
                  properties: {
                    name: { type: "string" },
                    closing: { type: "string", format: "date-time" },
                    subscriptionDeadline: {
                      type: "string",
                      format: "date-time",
                    },
                  },
                },
              },
              targets: {
                type: "object",
                properties: {
                  targetLeverage: {
                    type: "object",
                    properties: {
                      minimum: { type: "number" },
                      maximum: { type: "number" },
                    },
                  },
                  targetInvestmentSize: { $ref: "#/$defs/TargetInvestment" },
                  targetInternalRateOfReturn: {
                    type: "object",
                    properties: {
                      minimum: { type: "number" },
                      maximum: { type: "number" },
                      grossMinimum: { type: "number" },
                      grossMaximum: { type: "number" },
                    },
                  },
                  targetMultipleOnInvestedCapital: {
                    type: "object",
                    properties: {
                      netMinimum: { type: "number" },
                      netMaximum: { type: "number" },
                      grossMinimum: { type: "number" },
                      grossMaximum: { type: "number" },
                    },
                  },
                  targetNumberOfInvestments: {
                    $ref: "#/$defs/TargetInvestment",
                  },
                },
              },
              isEmployee: {
                type: "boolean",
              },
              footnotes: {
                type: "array",
                items: { $ref: "#/$defs/Footnote" },
              },
              customFields: {
                type: "array",
                items: { $ref: "#/$defs/CustomField" },
              },
              shareClasses: {
                type: "array",
                items: {
                  type: "object",
                  required: ["shareClassIdentifier"],
                  properties: {
                    shareClassIdentifier: {
                      type: "object",
                      required: ["code", "name"],
                      properties: {
                        code: { type: "string", minLength: 1 },
                        name: { type: "string", minLength: 1 },
                      },
                    },
                    investorTypes: {
                      type: "object",
                      properties: {
                        investorTypes: {
                          type: "array",
                          items: {
                            type: "string",
                            enum: [
                              "KNOWLEDGEABLE_EMPLOYEE",
                              "QUALIFIED_PURCHASER",
                              "ACCREDITED_INVESTOR",
                              "QUALIFIED_CLIENT",
                              "INSTITUTIONAL_INVESTOR",
                              "EU_PROFESSIONAL_CLIENT",
                              "RETIREMENT_PLAN",
                              "NON_ACCREDITED_OR_RETAIL",
                            ],
                          },
                        },
                      },
                    },
                    investorTargetRegion: {
                      type: "object",
                      properties: {
                        investorTargetRegion: {
                          type: "array",
                          items: {
                            type: "string",
                          },
                        },
                      },
                    },
                    nonQualifiedMinimumInvestment: {
                      type: "object",
                      properties: {
                        currencyAmount: { $ref: "#/$defs/CurrencyAmount" },
                      },
                    },
                    nonQualifiedMinimumSubsequentInvestment: {
                      type: "object",
                      properties: {
                        currencyAmount: { $ref: "#/$defs/CurrencyAmount" },
                      },
                    },
                    description: {
                      type: "object",
                      properties: {
                        description: { type: "string" },
                      },
                    },
                    carriedInterestRate: {
                      type: "object",
                      properties: {
                        carriedInterestRate: { type: "number" },
                        carriedInterestAdditionalInformation: {
                          type: "string",
                        },
                      },
                    },
                    managementFees: {
                      type: "object",
                      properties: {
                        managementFees: {
                          type: "array",
                          items: {
                            type: "object",
                            properties: {
                              rate: { $ref: "#/$defs/Rate" },
                              additionalInformation: { type: "string" },
                              name: { type: "string" },
                            },
                          },
                        },
                      },
                    },
                    incentiveFees: {
                      type: "array",
                      items: {
                        type: "object",
                        properties: {
                          fee: { $ref: "#/$defs/Fee" },
                          isHeadlineFee: { type: "boolean" },
                        },
                      },
                    },
                    additionalFees: {
                      type: "array",
                      items: {
                        type: "object",
                        properties: {
                          type: {
                            type: "string",
                            enum: [
                              "TRAIL",
                              "BROKERAGE",
                              "PLACEMENT",
                              "TRANSACTION",
                              "ADMINISTRATION",
                              "SERVICING",
                              "CUSTODY",
                              "SPONSOR",
                              "EXIT",
                              "INCENTIVE",
                              "PASS_THROUGH",
                              "SALES_LOAD",
                              "OTHER",
                            ],
                          },
                          fee: { $ref: "#/$defs/Fee" },
                        },
                      },
                    },
                    footnotes: {
                      type: "array",
                      items: { $ref: "#/$defs/Footnote" },
                    },
                    customFields: {
                      type: "object",
                      properties: {
                        customFields: {
                          type: "array",
                          items: { $ref: "#/$defs/CustomField" },
                        },
                      },
                    },
                    valuationFrequency: {
                      type: "object",
                      properties: {
                        frequency: { $ref: "#/$defs/Frequency" },
                      },
                    },
                    hurdleRate: {
                      type: "object",
                      properties: {
                        rate: { $ref: "#/$defs/Rate" },
                        additionalInformation: { type: "string" },
                      },
                    },
                    redemptionGates: {
                      type: "object",
                      properties: {
                        redemptionGate: { type: "number" },
                        additionalInformation: { type: "string" },
                      },
                    },
                    inceptionDate: {
                      $ref: "#/$defs/InceptionDate",
                    },
                  },
                },
              },
            },
          },
        },
        documents: {
          type: "object",
          required: ["fundDocuments"],
          properties: {
            fundDocuments: {
              type: "array",
              items: {
                type: "object",
                required: [
                  "name",
                  "fileName",
                  "link",
                  "documentCategories",
                  "type",
                ],
                properties: {
                  asOfDate: { type: "string", format: "date-time" },
                  name: { type: "string" },
                  fileName: { type: "string" },
                  link: { type: "string" },
                  documentCategories: {
                    type: "array",
                    items: {
                      type: "object",
                      required: ["id", "name"],
                      properties: {
                        id: { type: "string", minLength: 1 },
                        name: { type: "string", minLength: 1 },
                      },
                    },
                  },
                  type: {
                    type: "string",
                    enum: ["PDF", "PPT", "ZIP", "EXCEL"],
                  },
                  documentDescription: { type: "string" },
                  customFields: {
                    type: "array",
                    items: {
                      type: "object",
                      properties: {
                        name: { type: "string" },
                        key: { type: "string" },
                        documentFieldDataValues: {
                          type: "array",
                          items: {
                            type: "object",
                            properties: {
                              value: { type: "string" },
                              label: { type: "string" },
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
        medias: {
          type: "object",
          properties: {
            fundMedias: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  mediaName: { type: "string" },
                  mediaFileName: { type: "string" },
                  mediaUrl: { type: "string" },
                  mediaThumbnailUrl: { type: "string" },
                  mediaKey: { type: "string" },
                },
              },
            },
          },
        },
        fundManagerTeam: {
          type: "object",
          properties: {
            teamMembers: {
              type: "array",
              items: {
                type: "object",
                required: ["name"],
                properties: {
                  name: { type: "string" },
                  role: { type: "string" },
                  biography: { type: "string" },
                  externalLinks: {
                    type: "array",
                    items: {
                      type: "object",
                      properties: {
                        category: { type: "string" },
                        url: { type: "string" },
                        title: { type: "string" },
                      },
                    },
                  },
                  displayImage: {
                    type: "object",
                    properties: {
                      key: { type: "string" },
                      url: { type: "string" },
                    },
                  },
                },
              },
            },
          },
        },
        footnotes: {
          type: "array",
          items: { $ref: "#/$defs/Footnote" },
        },
        customFields: {
          type: "array",
          items: { $ref: "#/$defs/CustomField" },
        },
      },
    },
  },
};
